import { useState, useMemo } from 'react';
import { trpc } from '../lib/trpc';
import { useProject } from '../context/ProjectContext';
import {
  ComposedChart,
  Bar,
  BarChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

// Date range preset types
type DateRangePreset = 'last7days' | 'last30days' | 'thisMonth' | 'custom';

interface DateRange {
  startDate: string;
  endDate: string;
}

/**
 * Calculate date range based on preset selection
 */
function getDateRangeFromPreset(preset: DateRangePreset, customRange?: DateRange): DateRange {
  const now = new Date();
  const today = now.toISOString().split('T')[0];

  switch (preset) {
    case 'last7days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: today,
      };
    }
    case 'last30days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 29);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: today,
      };
    }
    case 'thisMonth': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: today,
      };
    }
    case 'custom': {
      return customRange ?? {
        startDate: today,
        endDate: today,
      };
    }
  }
}

/**
 * Format date range for display
 */
function formatDateRangeDisplay(range: DateRange): string {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  return `${formatDate(range.startDate)} - ${formatDate(range.endDate)}`;
}

interface DateRangePickerProps {
  selectedPreset: DateRangePreset;
  customRange: DateRange;
  onPresetChange: (preset: DateRangePreset) => void;
  onCustomRangeChange: (range: DateRange) => void;
}

/**
 * DateRangePicker component with preset options and custom date selection.
 * Allows users to select "Last 7 Days", "Last 30 Days", "This Month", or a custom range.
 */
function DateRangePicker({
  selectedPreset,
  customRange,
  onPresetChange,
  onCustomRangeChange,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const presets: { key: DateRangePreset; label: string }[] = [
    { key: 'last7days', label: 'Last 7 Days' },
    { key: 'last30days', label: 'Last 30 Days' },
    { key: 'thisMonth', label: 'This Month' },
    { key: 'custom', label: 'Custom Range' },
  ];

  const currentRange = getDateRangeFromPreset(selectedPreset, customRange);
  const displayText = selectedPreset === 'custom'
    ? formatDateRangeDisplay(customRange)
    : presets.find(p => p.key === selectedPreset)?.label ?? 'Select Range';

  const handlePresetSelect = (preset: DateRangePreset) => {
    onPresetChange(preset);
    if (preset !== 'custom') {
      setIsOpen(false);
    }
  };

  const handleCustomDateChange = (field: 'startDate' | 'endDate', value: string) => {
    onCustomRangeChange({
      ...customRange,
      [field]: value,
    });
  };

  const handleApplyCustomRange = () => {
    setIsOpen(false);
  };

  return (
    <div style={datePickerStyles.container}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={datePickerStyles.triggerButton}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '8px' }}>
          <path
            d="M5 1V3M11 1V3M2 6H14M3 2H13C13.5523 2 14 2.44772 14 3V13C14 13.5523 13.5523 14 13 14H3C2.44772 14 2 13.5523 2 13V3C2 2.44772 2.44772 2 3 2Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>{displayText}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          style={{ marginLeft: '8px', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
        >
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div style={datePickerStyles.backdrop} onClick={() => setIsOpen(false)} />

          {/* Dropdown Menu */}
          <div style={datePickerStyles.dropdown}>
            {/* Preset Options */}
            <div style={datePickerStyles.presetSection}>
              {presets.map((preset) => (
                <button
                  key={preset.key}
                  onClick={() => handlePresetSelect(preset.key)}
                  style={{
                    ...datePickerStyles.presetButton,
                    ...(selectedPreset === preset.key ? datePickerStyles.presetButtonActive : {}),
                  }}
                >
                  {selectedPreset === preset.key && (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: '8px' }}>
                      <path d="M2 7L5.5 10.5L12 4" stroke="#5A8A5E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Custom Date Inputs */}
            {selectedPreset === 'custom' && (
              <div style={datePickerStyles.customSection}>
                <div style={datePickerStyles.dateInputGroup}>
                  <label style={datePickerStyles.dateLabel}>From</label>
                  <input
                    type="date"
                    value={customRange.startDate}
                    onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                    style={datePickerStyles.dateInput}
                    max={customRange.endDate}
                  />
                </div>
                <div style={datePickerStyles.dateInputGroup}>
                  <label style={datePickerStyles.dateLabel}>To</label>
                  <input
                    type="date"
                    value={customRange.endDate}
                    onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                    style={datePickerStyles.dateInput}
                    min={customRange.startDate}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <button
                  onClick={handleApplyCustomRange}
                  style={datePickerStyles.applyButton}
                >
                  Apply
                </button>
              </div>
            )}

            {/* Current Range Display */}
            <div style={datePickerStyles.rangeDisplay}>
              <span style={datePickerStyles.rangeDisplayLabel}>Selected:</span>
              <span style={datePickerStyles.rangeDisplayValue}>{formatDateRangeDisplay(currentRange)}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// DateRangePicker styles
const datePickerStyles: Record<string, React.CSSProperties> = {
  container: {
    position: 'relative',
  },
  triggerButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '10px',
    backgroundColor: 'white',
    color: '#3D3A36',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: '"General Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  },
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 99,
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid rgba(93, 86, 78, 0.12)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    zIndex: 100,
    minWidth: '240px',
    overflow: 'hidden',
  },
  presetSection: {
    padding: '8px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
  },
  presetButton: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '10px 12px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    color: '#5C564E',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: '"General Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    cursor: 'pointer',
    textAlign: 'left' as const,
    transition: 'background-color 0.15s ease',
  },
  presetButtonActive: {
    backgroundColor: 'rgba(90, 138, 94, 0.08)',
    color: '#5A8A5E',
    fontWeight: 600,
  },
  customSection: {
    padding: '16px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  dateInputGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  dateLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#9B9285',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.02em',
  },
  dateInput: {
    padding: '10px 12px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '8px',
    fontSize: '13px',
    fontFamily: '"General Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#3D3A36',
    outline: 'none',
  },
  applyButton: {
    padding: '10px 16px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#5A8A5E',
    color: 'white',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: '"General Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    cursor: 'pointer',
    marginTop: '4px',
  },
  rangeDisplay: {
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'rgba(93, 86, 78, 0.03)',
  },
  rangeDisplayLabel: {
    fontSize: '11px',
    color: '#9B9285',
  },
  rangeDisplayValue: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#3D3A36',
  },
};

// Primary color for charts and highlights
const PRIMARY_COLOR = '#5A8A5E';

// Status colors for health metrics
const STATUS_COLORS = {
  green: { bg: 'rgba(90, 138, 94, 0.08)', text: '#5A8A5E', border: 'rgba(90, 138, 94, 0.2)' },
  yellow: { bg: 'rgba(155, 123, 60, 0.08)', text: '#9B7B3C', border: 'rgba(155, 123, 60, 0.2)' },
  red: { bg: 'rgba(139, 90, 80, 0.08)', text: '#8B5A50', border: 'rgba(139, 90, 80, 0.2)' },
};


// Sample churn breakdown data bucketed by active lifespan periods
const CHURN_BREAKDOWN_DATA = [
  { period: '0-7 days', count: 145, color: '#8B5A50' },    // Early churn - critical
  { period: '8-30 days', count: 89, color: '#C9A090' },    // Mid-early churn
  { period: '31-60 days', count: 52, color: '#9B7B3C' },   // Mid churn
  { period: '60+ days', count: 28, color: '#5A8A5E' },     // Late churn - healthier
];

// Fallback cohort data for when API data is not available
const FALLBACK_COHORT_DATA = [
  { month: 'Aug 2024', m0: 100, m1: 92, m2: 87, m3: 84, m4: 82 },
  { month: 'Sep 2024', m0: 100, m1: 89, m2: 85, m3: 81, m4: null },
  { month: 'Oct 2024', m0: 100, m1: 91, m2: 86, m3: null, m4: null },
  { month: 'Nov 2024', m0: 100, m1: 88, m2: null, m3: null, m4: null },
  { month: 'Dec 2024', m0: 100, m1: null, m2: null, m3: null, m4: null },
];

interface SaaSHealthScorecardProps {
  mrrGrowthRate: number;
  nrr: number;
  churn30Day: number;
  cacPayback: number;
  grossMargin: number;
}

function SaaSHealthScorecard({ mrrGrowthRate, nrr, churn30Day, cacPayback, grossMargin }: SaaSHealthScorecardProps) {
  // Determine trend direction based on metric type
  // For churn, lower is better (trend down is good)
  // For others, higher is better (trend up is good)
  const metrics = [
    {
      label: 'MRR Growth',
      value: `${mrrGrowthRate.toFixed(1)}%`,
      target: '≥8%',
      status: mrrGrowthRate >= 8 ? 'green' : mrrGrowthRate >= 3 ? 'yellow' : 'red',
      icon: '📈',
      trend: mrrGrowthRate >= 8 ? 'up' : 'down',
      isPositiveTrend: mrrGrowthRate >= 8,
    },
    {
      label: 'NRR',
      value: `${nrr}%`,
      target: '≥100%',
      status: nrr >= 100 ? 'green' : 'red',
      icon: '🔄',
      trend: nrr >= 100 ? 'up' : 'down',
      isPositiveTrend: nrr >= 100,
    },
    {
      label: '30-Day Churn',
      value: `${churn30Day}%`,
      target: '<4%',
      status: churn30Day < 2 ? 'green' : churn30Day <= 4 ? 'yellow' : 'red',
      icon: '📉',
      // For churn, lower is better - so showing down arrow when good
      trend: churn30Day <= 4 ? 'down' : 'up',
      isPositiveTrend: churn30Day <= 4,
    },
    {
      label: 'CAC Payback',
      value: `${cacPayback}mo`,
      target: '<12mo',
      status: cacPayback < 12 ? 'green' : cacPayback <= 18 ? 'yellow' : 'red',
      icon: '⏱️',
      // For CAC payback, lower is better
      trend: cacPayback < 12 ? 'down' : 'up',
      isPositiveTrend: cacPayback < 12,
    },
    {
      label: 'Gross Margin',
      value: `${grossMargin}%`,
      target: '≥80%',
      status: grossMargin >= 80 ? 'green' : grossMargin >= 70 ? 'yellow' : 'red',
      icon: '💵',
      trend: grossMargin >= 80 ? 'up' : 'down',
      isPositiveTrend: grossMargin >= 80,
    },
  ] as const;

  const healthyCount = metrics.filter(m => m.status === 'green').length;

  return (
    <div style={styles.section}>
      <div style={styles.healthScorecardHeader}>
        <h3 style={styles.chartTitle}>SaaS Health Scorecard</h3>
        <span style={styles.healthyBadge}>
          {healthyCount}/{metrics.length} Healthy Metrics
        </span>
      </div>
      {/* Using CSS class for responsive grid */}
      <div className="saas-health-scorecard-grid">
        {metrics.map((m, i) => {
          const c = STATUS_COLORS[m.status];
          const trendColor = m.isPositiveTrend ? '#5A8A5E' : '#8B5A50';
          return (
            <div
              key={i}
              style={{
                padding: '16px',
                borderRadius: '12px',
                background: c.bg,
                border: `1px solid ${c.border}`,
              }}
            >
              <div style={styles.healthMetricHeader}>
                <span style={{ fontSize: '16px' }}>{m.icon}</span>
                <span style={styles.healthMetricLabel}>{m.label}</span>
              </div>
              <div style={styles.healthMetricValueRow}>
                <p style={{ ...styles.healthMetricValue, color: c.text, margin: 0 }}>{m.value}</p>
                {/* Trend indicator arrow */}
                <span style={{ ...styles.trendIndicator, color: trendColor }}>
                  {m.trend === 'up' ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 4L12 8L10.6 9.4L9 7.8V12H7V7.8L5.4 9.4L4 8L8 4Z" fill={trendColor}/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 12L4 8L5.4 6.6L7 8.2V4H9V8.2L10.6 6.6L12 8L8 12Z" fill={trendColor}/>
                    </svg>
                  )}
                </span>
              </div>
              <div style={styles.healthMetricTarget}>
                <svg width="8" height="8" viewBox="0 0 8 8">
                  <circle cx="4" cy="4" r="4" fill={c.text} />
                </svg>
                <span style={styles.healthMetricTargetText}>Target: {m.target}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface CohortRetentionTableProps {
  data?: Array<{
    month: string;
    cohortSize: number;
    retentionByMonth: Array<number | null>;
  }>;
  summary?: {
    avgM1Retention: number;
    avgM3Retention: number;
    industryBenchmark: number;
    totalCohorts: number;
  };
  isLoading?: boolean;
}

function CohortRetentionTable({ data, summary, isLoading }: CohortRetentionTableProps) {
  // Heatmap-style coloring: opacity scales with retention value (0-100)
  // Higher retention = more opaque/darker background
  const getRetentionColor = (val: number | null) => {
    if (val === null) return { color: 'transparent', bg: 'transparent', opacity: 0 };
    // Scale opacity from 0.1 (at 0%) to 0.6 (at 100%)
    const opacity = 0.1 + (val / 100) * 0.5;
    // Use primary green color with varying opacity for heatmap effect
    return {
      color: val >= 80 ? '#3D5A40' : val >= 60 ? '#5C564E' : '#8B5A50',
      bg: `rgba(90, 138, 94, ${opacity})`,
      opacity
    };
  };

  // Use API data if available, otherwise use fallback
  const cohortData = data && data.length > 0 ? data : null;
  const avgM3 = summary?.avgM3Retention ?? 0;
  const benchmark = summary?.industryBenchmark ?? 80;
  const benchmarkDiff = avgM3 - benchmark;

  // Transform API data to table format or use fallback
  const tableRows = cohortData
    ? cohortData.map(cohort => ({
        month: cohort.month,
        values: cohort.retentionByMonth,
      }))
    : FALLBACK_COHORT_DATA.map(row => ({
        month: row.month,
        values: [row.m0, row.m1, row.m2, row.m3, row.m4] as Array<number | null>,
      }));

  // Calculate fallback avgM3 if no API data
  const displayAvgM3 = cohortData
    ? avgM3
    : Math.round(
        FALLBACK_COHORT_DATA
          .map(r => r.m3)
          .filter((v): v is number => v !== null)
          .reduce((a, b) => a + b, 0) /
          FALLBACK_COHORT_DATA.filter(r => r.m3 !== null).length
      );

  // Determine number of months from data
  const monthCount = tableRows.length > 0 ? tableRows[0].values.length : 5;

  if (isLoading) {
    return (
      <div style={styles.cohortCard}>
        <div style={styles.chartHeaderSimple}>
          <h3 style={styles.chartTitle}>Cohort Retention</h3>
          <p style={styles.chartSubtitle}>% of users retained by signup month</p>
        </div>
        <div style={styles.cohortLoading}>Loading cohort data...</div>
      </div>
    );
  }

  return (
    <div style={styles.cohortCard}>
      <div style={styles.chartHeaderSimple}>
        <h3 style={styles.chartTitle}>Cohort Retention</h3>
        <p style={styles.chartSubtitle}>% of users retained by signup month</p>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={styles.cohortTable}>
          <thead>
            <tr>
              <th style={{ ...styles.cohortTh, borderRadius: '6px 0 0 6px' }}>Cohort</th>
              {Array.from({ length: monthCount }, (_, i) => (
                <th
                  key={i}
                  style={{
                    ...styles.cohortTh,
                    ...(i === monthCount - 1 ? { borderRadius: '0 6px 6px 0' } : {}),
                  }}
                >
                  Month {i + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, i) => (
              <tr key={i}>
                <td style={styles.cohortTdMonth}>{row.month}</td>
                {row.values.map((val, j) => {
                  const colors = getRetentionColor(val);
                  return (
                    <td key={j} style={styles.cohortTd}>
                      {val !== null ? (
                        <span
                          style={{
                            padding: '4px 10px',
                            borderRadius: '6px',
                            background: colors.bg,
                            color: colors.color,
                            fontWeight: 600,
                          }}
                        >
                          {val}%
                        </span>
                      ) : (
                        <span style={{ color: '#D4D0CA' }}>—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={styles.cohortInsight}>
        <p style={styles.cohortInsightText}>
          {benchmarkDiff >= 0 ? (
            <>💡 Avg M3 retention is <strong>{displayAvgM3}%</strong> — {benchmarkDiff}% above industry benchmark</>
          ) : (
            <>📉 Avg M3 retention is <strong>{displayAvgM3}%</strong> — {Math.abs(benchmarkDiff)}% below industry benchmark</>
          )}
        </p>
      </div>
    </div>
  );
}

interface UnitEconomicsProps {
  ltv: number;
  cac: number;
}

/**
 * UnitEconomics component displays core unit economics metrics:
 * - Lifetime Value (LTV)
 * - Customer Acquisition Cost (CAC)
 * - LTV:CAC Ratio with visual health indicators
 *
 * Health indicator colors:
 * - Green: ratio >= 3.0 (healthy)
 * - Yellow: ratio >= 1.0 and < 3.0 (needs improvement)
 * - Red: ratio < 1.0 (critical)
 */
function UnitEconomics({ ltv, cac }: UnitEconomicsProps) {
  const ratio = cac > 0 ? ltv / cac : 0;

  // Determine health status based on LTV:CAC ratio thresholds
  const getHealthStatus = (ratioValue: number): 'green' | 'yellow' | 'red' => {
    if (ratioValue >= 3.0) return 'green';
    if (ratioValue >= 1.0) return 'yellow';
    return 'red';
  };

  const healthStatus = getHealthStatus(ratio);
  const colors = STATUS_COLORS[healthStatus];

  // Health status labels
  const getHealthLabel = (status: 'green' | 'yellow' | 'red'): string => {
    switch (status) {
      case 'green':
        return '✓ Healthy';
      case 'yellow':
        return '⚠ Needs Improvement';
      case 'red':
        return '✗ Critical';
    }
  };

  return (
    <div style={styles.unitEconomicsCard}>
      <h3 style={{ ...styles.chartTitle, marginBottom: '20px' }}>Unit Economics</h3>
      <div style={styles.unitEconomicsGrid}>
        <div style={styles.unitEconomicsMetric}>
          <p style={styles.unitEconomicsLabel}>Lifetime Value (LTV)</p>
          <p style={styles.unitEconomicsValue}>${ltv.toLocaleString()}</p>
        </div>
        <div style={styles.unitEconomicsMetric}>
          <p style={styles.unitEconomicsLabel}>Customer Acquisition Cost (CAC)</p>
          <p style={styles.unitEconomicsValue}>${cac.toLocaleString()}</p>
        </div>
      </div>
      <div
        style={{
          padding: '16px',
          borderRadius: '12px',
          background: colors.bg,
          border: `1px solid ${colors.border}`,
        }}
      >
        <div style={styles.ratioContainer}>
          <div>
            <p style={styles.ratioLabel}>LTV:CAC Ratio</p>
            <p style={{ ...styles.ratioValue, color: colors.text }}>
              {ratio.toFixed(1)}:1
            </p>
          </div>
          <span
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              background: healthStatus === 'green'
                ? 'rgba(90, 138, 94, 0.15)'
                : healthStatus === 'yellow'
                ? 'rgba(155, 123, 60, 0.15)'
                : 'rgba(139, 90, 80, 0.15)',
              fontSize: '12px',
              fontWeight: 600,
              color: colors.text,
            }}
          >
            {getHealthLabel(healthStatus)}
          </span>
        </div>
        <p style={styles.ratioHint}>
          {healthStatus === 'green'
            ? 'Ratio ≥ 3:1 indicates sustainable unit economics'
            : healthStatus === 'yellow'
            ? 'Ratio should be ≥ 3:1 for healthy acquisition'
            : 'Ratio < 1:1 means acquisition cost exceeds lifetime value'}
        </p>
      </div>
    </div>
  );
}

/**
 * Interface for funnel stage data from API
 */
interface FunnelStage {
  stage: string;
  users: number;
  label: string;
  description: string;
}

/**
 * Onboarding Funnel visualization component
 * Shows conversion rates between steps with horizontal bars and drop-off percentages
 */
interface OnboardingFunnelProps {
  data: FunnelStage[];
}

function OnboardingFunnel({ data }: OnboardingFunnelProps) {
  // If no data, show empty state
  if (!data || data.length === 0) {
    return (
      <div style={styles.funnelCard}>
        <div style={styles.funnelHeader}>
          <div>
            <h3 style={styles.chartTitle}>Onboarding Funnel</h3>
            <p style={styles.chartSubtitle}>User conversion through signup flow</p>
          </div>
        </div>
        <div style={{ padding: '40px', textAlign: 'center', color: '#9B9285' }}>
          No funnel data available
        </div>
      </div>
    );
  }

  const maxUsers = data[0].users;

  // Calculate conversion rates between steps
  const stepsWithRates = data.map((stage, index) => {
    const conversionRate = maxUsers > 0 ? (stage.users / maxUsers) * 100 : 0;
    const dropOffRate = index > 0 && data[index - 1].users > 0
      ? ((data[index - 1].users - stage.users) / data[index - 1].users) * 100
      : 0;
    return { step: stage.stage, users: stage.users, label: stage.label, conversionRate, dropOffRate };
  });

  // Overall conversion rate
  const overallConversion = maxUsers > 0
    ? ((data[data.length - 1].users / maxUsers) * 100).toFixed(1)
    : '0.0';

  return (
    <div style={styles.funnelCard}>
      <div style={styles.funnelHeader}>
        <div>
          <h3 style={styles.chartTitle}>Onboarding Funnel</h3>
          <p style={styles.chartSubtitle}>User conversion through signup flow</p>
        </div>
        <div style={styles.funnelOverallRate}>
          <span style={styles.funnelOverallLabel}>Overall Conversion</span>
          <span style={styles.funnelOverallValue}>{overallConversion}%</span>
        </div>
      </div>

      <div style={styles.funnelSteps}>
        {stepsWithRates.map((step, index) => (
          <div key={step.step} style={styles.funnelStepRow}>
            {/* Step Info */}
            <div style={styles.funnelStepInfo}>
              <span style={styles.funnelStepNumber}>{index + 1}</span>
              <div>
                <p style={styles.funnelStepName}>{step.step}</p>
                <p style={styles.funnelStepLabel}>{step.label}</p>
              </div>
            </div>

            {/* Funnel Bar */}
            <div style={styles.funnelBarWrapper}>
              <div style={styles.funnelBarBackground}>
                <div
                  style={{
                    ...styles.funnelBar,
                    width: `${step.conversionRate}%`,
                    background: index === stepsWithRates.length - 1
                      ? PRIMARY_COLOR
                      : `linear-gradient(90deg, ${PRIMARY_COLOR} 0%, ${PRIMARY_COLOR}99 100%)`,
                  }}
                />
              </div>
              {/* Drop-off indicator between steps */}
              {index > 0 && step.dropOffRate > 0 && (
                <div style={styles.dropOffIndicator}>
                  <svg width="12" height="12" viewBox="0 0 12 12">
                    <path d="M6 2 L6 10 M3 7 L6 10 L9 7" stroke="#C9A090" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span style={styles.dropOffText}>-{step.dropOffRate.toFixed(1)}%</span>
                </div>
              )}
            </div>

            {/* User Count */}
            <div style={styles.funnelUserCount}>
              <span style={styles.funnelUserValue}>{step.users.toLocaleString()}</span>
              <span style={styles.funnelConversionPercent}>{step.conversionRate.toFixed(1)}%</span>
            </div>
          </div>
        ))}
      </div>

      {/* Funnel Insights */}
      <div style={styles.funnelInsights}>
        {stepsWithRates.length > 1 && (() => {
          // Find the biggest drop-off
          const dropOffs = stepsWithRates.slice(1).map((step, idx) => ({
            from: stepsWithRates[idx].step,
            to: step.step,
            rate: step.dropOffRate,
          }));
          const biggestDropOff = dropOffs.reduce((max, curr) => curr.rate > max.rate ? curr : max, dropOffs[0]);
          return (
            <div style={styles.funnelInsightItem}>
              <span style={styles.funnelInsightIcon}>🎯</span>
              <span style={styles.funnelInsightText}>
                Biggest drop-off at <strong>{biggestDropOff.from} → {biggestDropOff.to}</strong> ({biggestDropOff.rate.toFixed(1)}%)
              </span>
            </div>
          );
        })()}
        <div style={styles.funnelInsightItem}>
          <span style={styles.funnelInsightIcon}>📈</span>
          <span style={styles.funnelInsightText}>
            <strong>{overallConversion}%</strong> of visitors become active users
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Interface for weekly activation data from API
 */
interface WeeklyActivationDataItem {
  week: string;
  activationRate: number;
  target: number;
}

/**
 * Weekly Activation Trend chart component
 * Shows activation trends over multiple weeks with target comparison
 */
interface WeeklyActivationTrendProps {
  data: WeeklyActivationDataItem[];
}

function WeeklyActivationTrend({ data }: WeeklyActivationTrendProps) {
  // If no data, show empty state
  if (!data || data.length === 0) {
    return (
      <div style={styles.activationCard}>
        <div style={styles.chartHeader}>
          <div>
            <h3 style={styles.chartTitle}>Weekly Activation Trend</h3>
            <p style={styles.chartSubtitle}>Activation rate per week</p>
          </div>
        </div>
        <div style={{ padding: '40px', textAlign: 'center', color: '#9B9285' }}>
          No activation data available
        </div>
      </div>
    );
  }

  // Use activation rate as the metric (percentage)
  const maxActivations = Math.max(...data.map(d => Math.max(d.activationRate, d.target)), 100);
  const avgActivations = Math.round(data.reduce((sum, d) => sum + d.activationRate, 0) / data.length);
  const targetHits = data.filter(d => d.activationRate >= d.target).length;

  // Calculate horizontal spacing based on data length
  const chartWidth = 420; // viewBox width minus margins
  const spacing = data.length > 1 ? chartWidth / (data.length - 1) : chartWidth;
  const startX = 70;

  return (
    <div style={styles.activationCard}>
      <div style={styles.chartHeader}>
        <div>
          <h3 style={styles.chartTitle}>Weekly Activation Trend</h3>
          <p style={styles.chartSubtitle}>Activation rate per week</p>
        </div>
        <div style={styles.legend}>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendColor, background: PRIMARY_COLOR }} />
            <span style={styles.legendLabel}>Activation Rate</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendColor, background: '#D4D0CA', borderRadius: '50%' }} />
            <span style={styles.legendLabel}>Target</span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div style={styles.activationChartContainer}>
        <svg width="100%" height="180" viewBox="0 0 500 180" preserveAspectRatio="none">
          {/* Grid lines */}
          {[0, 1, 2, 3].map(i => (
            <line key={i} x1="40" y1={i * 45 + 20} x2="480" y2={i * 45 + 20} stroke="rgba(93, 86, 78, 0.06)" strokeWidth="1"/>
          ))}

          {/* Target line (dashed) */}
          <path
            d={`M ${data.map((d, i) => `${startX + i * spacing} ${160 - (d.target / maxActivations) * 130}`).join(' L ')}`}
            fill="none"
            stroke="#D4D0CA"
            strokeWidth="2"
            strokeDasharray="6,4"
            strokeLinecap="round"
          />

          {/* Activations area fill */}
          <path
            d={`M ${startX} 160 ${data.map((d, i) => `L ${startX + i * spacing} ${160 - (d.activationRate / maxActivations) * 130}`).join(' ')} L ${startX + (data.length - 1) * spacing} 160 Z`}
            fill={`${PRIMARY_COLOR}15`}
          />

          {/* Activations line */}
          <path
            d={`M ${data.map((d, i) => `${startX + i * spacing} ${160 - (d.activationRate / maxActivations) * 130}`).join(' L ')}`}
            fill="none"
            stroke={PRIMARY_COLOR}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data points */}
          {data.map((d, i) => {
            const x = startX + i * spacing;
            const y = 160 - (d.activationRate / maxActivations) * 130;
            const aboveTarget = d.activationRate >= d.target;
            return (
              <g key={i}>
                <circle
                  cx={x}
                  cy={y}
                  r="6"
                  fill="white"
                  stroke={aboveTarget ? PRIMARY_COLOR : '#C9A090'}
                  strokeWidth="2.5"
                />
                {aboveTarget && (
                  <circle cx={x} cy={y} r="3" fill={PRIMARY_COLOR} />
                )}
              </g>
            );
          })}
        </svg>

        {/* X-axis labels */}
        <div style={styles.activationXLabels}>
          {data.map((d, i) => (
            <div key={i} style={styles.activationXLabel}>
              <span style={styles.axisLabelText}>{d.week}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Summary stats */}
      <div style={styles.activationSummary}>
        <div style={styles.activationSummaryItem}>
          <p style={styles.activationSummaryLabel}>Weeks Tracked</p>
          <p style={styles.activationSummaryValue}>{data.length}</p>
        </div>
        <div style={styles.activationSummaryItem}>
          <p style={styles.activationSummaryLabel}>Avg Activation</p>
          <p style={styles.activationSummaryValue}>{avgActivations}%</p>
        </div>
        <div style={styles.activationSummaryItem}>
          <p style={styles.activationSummaryLabel}>Target Hit Rate</p>
          <p style={{ ...styles.activationSummaryValue, color: targetHits === data.length ? '#5A8A5E' : '#9B7B3C' }}>
            {targetHits}/{data.length} weeks
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Churn Breakdown by Period chart component.
 * Displays a Pie/Donut chart showing churned users grouped by their active lifespan.
 * Periods: 0-7 days, 8-30 days, 31-60 days, 60+ days.
 */
function ChurnBreakdownChart() {
  // Calculate total churn count for percentage calculations
  const totalChurn = CHURN_BREAKDOWN_DATA.reduce((sum, item) => sum + item.count, 0);

  // Check if there's data to display
  const hasData = totalChurn > 0;

  // Custom tooltip component showing absolute count and percentage of total churn
  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload: { period: string; count: number; color: string } }>;
  }) => {
    if (active && payload && payload.length > 0) {
      const item = payload[0].payload;
      const percentage = totalChurn > 0 ? ((item.count / totalChurn) * 100).toFixed(1) : '0';
      return (
        <div style={styles.churnTooltip}>
          <p style={{ ...styles.churnTooltipTitle, color: item.color }}>{item.period}</p>
          <p style={styles.churnTooltipValue}>{item.count.toLocaleString()} users</p>
          <p style={styles.churnTooltipPercent}>{percentage}% of total churn</p>
        </div>
      );
    }
    return null;
  };

  // Find the highest churn period for insights
  const highestChurnPeriod = CHURN_BREAKDOWN_DATA.reduce((max, item) =>
    item.count > max.count ? item : max
  );
  const highestPercentage = totalChurn > 0
    ? ((highestChurnPeriod.count / totalChurn) * 100).toFixed(0)
    : '0';

  return (
    <div style={styles.churnBreakdownCard}>
      <div style={styles.chartHeaderSimple}>
        <h3 style={styles.chartTitle}>Churn Breakdown by Period</h3>
        <p style={styles.chartSubtitle}>When users churn by active lifespan</p>
      </div>

      {/* Pie Chart (styled as donut) */}
      <div style={styles.churnChartContainer}>
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={CHURN_BREAKDOWN_DATA}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={75}
                paddingAngle={2}
                dataKey="count"
                nameKey="period"
                stroke="none"
              >
                {CHURN_BREAKDOWN_DATA.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div style={styles.churnNoDataContainer}>
            <span style={{ fontSize: '24px' }}>📊</span>
            <p style={styles.churnNoDataText}>No churn data available</p>
          </div>
        )}

        {/* Center label showing total */}
        {hasData && (
          <div style={styles.churnCenterLabel}>
            <span style={styles.churnCenterValue}>{totalChurn}</span>
            <span style={styles.churnCenterText}>Total Churned</span>
          </div>
        )}
      </div>

      {/* Legend */}
      {hasData && (
        <div style={styles.churnLegend}>
          {CHURN_BREAKDOWN_DATA.map((item) => {
            const percentage = ((item.count / totalChurn) * 100).toFixed(1);
            return (
              <div key={item.period} style={styles.churnLegendItem}>
                <span
                  style={{
                    ...styles.churnLegendDot,
                    backgroundColor: item.color,
                  }}
                />
                <span style={styles.churnLegendLabel}>{item.period}</span>
                <span style={styles.churnLegendValue}>
                  {item.count} ({percentage}%)
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Insight */}
      {hasData && (
        <div style={styles.churnInsight}>
          <p style={styles.churnInsightText}>
            ⚠️ <strong>{highestPercentage}%</strong> of churn happens within{' '}
            <strong>{highestChurnPeriod.period}</strong> — focus on early retention
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Interface for tool usage data from API
 */
interface ToolUsageData {
  toolId: string;
  toolName: string;
  totalExecutions: number;
  successfulExecutions: number;
  failedExecutions: number;
  successRate: number;
  avgLatencyMs: number | null;
  minLatencyMs: number | null;
  maxLatencyMs: number | null;
}

interface ToolUsageSummary {
  totalExecutions: number;
  overallSuccessRate: number;
  avgLatencyMs: number | null;
  mostUsedTool: string | null;
  leastReliableTool: string | null;
}

interface ToolUsageChartProps {
  data: ToolUsageData[];
  summary: ToolUsageSummary;
  isLoading?: boolean;
}

/**
 * ToolUsageChart component displays tool analytics with:
 * - Bar chart showing executions per tool
 * - Table showing latency and success rates
 */
function ToolUsageChart({ data, summary, isLoading }: ToolUsageChartProps) {
  // Sort data by total executions for the bar chart
  const sortedData = [...data].sort((a, b) => b.totalExecutions - a.totalExecutions);

  // Custom tooltip for bar chart
  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload: ToolUsageData }>;
  }) => {
    if (active && payload && payload.length > 0) {
      const item = payload[0].payload;
      return (
        <div style={styles.toolTooltip}>
          <p style={styles.toolTooltipTitle}>{item.toolName}</p>
          <p style={styles.toolTooltipValue}>{item.totalExecutions.toLocaleString()} executions</p>
          <p style={styles.toolTooltipPercent}>
            {item.successRate}% success rate
          </p>
          {item.avgLatencyMs !== null && (
            <p style={styles.toolTooltipLatency}>
              {item.avgLatencyMs.toFixed(1)}ms avg latency
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Get health status color based on success rate
  const getSuccessRateStatus = (rate: number): 'green' | 'yellow' | 'red' => {
    if (rate >= 95) return 'green';
    if (rate >= 80) return 'yellow';
    return 'red';
  };

  // Get latency status color
  const getLatencyStatus = (latency: number | null): 'green' | 'yellow' | 'red' => {
    if (latency === null) return 'green';
    if (latency <= 500) return 'green';
    if (latency <= 2000) return 'yellow';
    return 'red';
  };

  if (isLoading) {
    return (
      <div style={styles.toolAnalyticsCard}>
        <div style={styles.chartHeaderSimple}>
          <h3 style={styles.chartTitle}>Tool Usage Analytics</h3>
          <p style={styles.chartSubtitle}>Execution counts and performance metrics</p>
        </div>
        <div style={{ padding: '40px', textAlign: 'center', color: '#9B9285' }}>
          Loading tool usage data...
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={styles.toolAnalyticsCard}>
        <div style={styles.chartHeaderSimple}>
          <h3 style={styles.chartTitle}>Tool Usage Analytics</h3>
          <p style={styles.chartSubtitle}>Execution counts and performance metrics</p>
        </div>
        <div style={{ padding: '40px', textAlign: 'center', color: '#9B9285' }}>
          No tool usage data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.toolAnalyticsCard}>
      <div style={styles.toolAnalyticsHeader}>
        <div>
          <h3 style={styles.chartTitle}>Tool Usage Analytics</h3>
          <p style={styles.chartSubtitle}>Execution counts and performance metrics</p>
        </div>
        <div style={styles.toolSummaryBadges}>
          <span style={styles.toolSummaryBadge}>
            {summary.totalExecutions.toLocaleString()} total executions
          </span>
          <span style={{
            ...styles.toolSummaryBadge,
            ...(summary.overallSuccessRate >= 95 ? styles.toolBadgeGreen :
                summary.overallSuccessRate >= 80 ? styles.toolBadgeYellow :
                styles.toolBadgeRed),
          }}>
            {summary.overallSuccessRate}% success rate
          </span>
        </div>
      </div>

      <div style={styles.toolAnalyticsGrid}>
        {/* Bar Chart for Executions */}
        <div style={styles.toolChartSection}>
          <h4 style={styles.toolSubsectionTitle}>Executions by Tool</h4>
          <div style={{ height: '220px', width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sortedData.slice(0, 8)} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <XAxis
                  dataKey="toolName"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: '#5C564E' }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  interval={0}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: '#9B9285' }}
                  width={50}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(93, 86, 78, 0.05)' }} />
                <Bar
                  dataKey="totalExecutions"
                  radius={[6, 6, 0, 0]}
                  maxBarSize={50}
                >
                  {sortedData.slice(0, 8).map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={index === 0 ? PRIMARY_COLOR : `${PRIMARY_COLOR}${Math.max(30, 100 - index * 10).toString(16).padStart(2, '0')}`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Table for Latency and Success Rates */}
        <div style={styles.toolTableSection}>
          <h4 style={styles.toolSubsectionTitle}>Performance Metrics</h4>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.toolTable}>
              <thead>
                <tr>
                  <th style={{ ...styles.toolTh, textAlign: 'left', borderRadius: '6px 0 0 6px' }}>Tool</th>
                  <th style={styles.toolTh}>Success</th>
                  <th style={styles.toolTh}>Avg Latency</th>
                  <th style={{ ...styles.toolTh, borderRadius: '0 6px 6px 0' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.slice(0, 6).map((tool) => {
                  const successStatus = getSuccessRateStatus(tool.successRate);
                  const latencyStatus = getLatencyStatus(tool.avgLatencyMs);
                  const overallStatus = successStatus === 'red' || latencyStatus === 'red'
                    ? 'red'
                    : successStatus === 'yellow' || latencyStatus === 'yellow'
                      ? 'yellow'
                      : 'green';
                  const statusColors = STATUS_COLORS[overallStatus];

                  return (
                    <tr key={tool.toolId}>
                      <td style={styles.toolTdName}>
                        <span style={styles.toolNameText}>{tool.toolName}</span>
                        <span style={styles.toolExecCount}>{tool.totalExecutions.toLocaleString()} runs</span>
                      </td>
                      <td style={styles.toolTd}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '6px',
                          background: STATUS_COLORS[successStatus].bg,
                          color: STATUS_COLORS[successStatus].text,
                          fontWeight: 600,
                          fontSize: '12px',
                        }}>
                          {tool.successRate}%
                        </span>
                      </td>
                      <td style={styles.toolTd}>
                        {tool.avgLatencyMs !== null ? (
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            background: STATUS_COLORS[latencyStatus].bg,
                            color: STATUS_COLORS[latencyStatus].text,
                            fontWeight: 500,
                            fontSize: '12px',
                          }}>
                            {tool.avgLatencyMs.toFixed(0)}ms
                          </span>
                        ) : (
                          <span style={{ color: '#9B9285', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td style={styles.toolTd}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          background: statusColors.bg,
                          color: statusColors.text,
                          fontSize: '11px',
                          fontWeight: 600,
                        }}>
                          {overallStatus === 'green' ? '✓ Healthy' :
                           overallStatus === 'yellow' ? '⚠ Warning' :
                           '✗ Critical'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Insights */}
      <div style={styles.toolInsights}>
        {summary.mostUsedTool && (
          <div style={styles.toolInsightItem}>
            <span style={styles.toolInsightIcon}>🔧</span>
            <span style={styles.toolInsightText}>
              Most used: <strong>{summary.mostUsedTool}</strong>
            </span>
          </div>
        )}
        {summary.leastReliableTool && summary.overallSuccessRate < 100 && (
          <div style={styles.toolInsightItem}>
            <span style={styles.toolInsightIcon}>⚠️</span>
            <span style={styles.toolInsightText}>
              Needs attention: <strong>{summary.leastReliableTool}</strong>
            </span>
          </div>
        )}
        {summary.avgLatencyMs !== null && (
          <div style={styles.toolInsightItem}>
            <span style={styles.toolInsightIcon}>⏱️</span>
            <span style={styles.toolInsightText}>
              Avg latency: <strong>{summary.avgLatencyMs.toFixed(1)}ms</strong>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Format currency values for axis labels
 */
function formatCurrency(value: number): string {
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k`;
  }
  return `$${value}`;
}

/**
 * Format percentage values for axis labels
 */
function formatPercentage(value: number): string {
  return `${value.toFixed(0)}%`;
}

/**
 * MRR Growth Trend chart component using Recharts ComposedChart.
 * Displays bar series for MRR totals and line series for month-over-month growth percentage.
 * Uses dual Y-axes: currency for MRR (left) and percentage for growth rate (right).
 */
interface MRRGrowthTrendChartProps {
  data: Array<{ month: string; mrr: number; growthRate: number }>;
  mrrGrowthAmount: number;
  mrrGrowthRate: number;
}

function MRRGrowthTrendChart({ data, mrrGrowthAmount, mrrGrowthRate }: MRRGrowthTrendChartProps) {
  // Custom tooltip showing both metrics simultaneously
  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number; dataKey: string }>; label?: string }) => {
    if (active && payload && payload.length) {
      const mrr = payload.find(p => p.dataKey === 'mrr')?.value ?? 0;
      const growthRate = payload.find(p => p.dataKey === 'growthRate')?.value ?? 0;
      return (
        <div style={{
          background: 'white',
          border: '1px solid rgba(93, 86, 78, 0.12)',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}>
          <p style={{ fontSize: '13px', fontWeight: 600, color: '#3D3A36', marginBottom: '8px' }}>{label}</p>
          <p style={{ fontSize: '12px', color: PRIMARY_COLOR, margin: '4px 0' }}>
            MRR: {formatCurrency(mrr)}
          </p>
          <p style={{ fontSize: '12px', color: '#9B7B3C', margin: '4px 0' }}>
            Growth Rate: {growthRate >= 0 ? '+' : ''}{growthRate.toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={styles.mrrChartCard}>
      <div style={styles.chartHeader}>
        <div>
          <h3 style={styles.chartTitle}>MRR Growth Trend</h3>
          <p style={styles.chartSubtitle}>Monthly recurring revenue with growth rate</p>
        </div>
        <div style={styles.legend}>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendColor, background: PRIMARY_COLOR }} />
            <span style={styles.legendLabel}>MRR</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendColor, background: '#9B7B3C', borderRadius: '50%' }} />
            <span style={styles.legendLabel}>Growth %</span>
          </div>
        </div>
      </div>

      {/* Recharts ComposedChart with dual Y-axes */}
      <div style={{ height: '220px', width: '100%' }}>
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 20, right: 60, left: 10, bottom: 5 }}>
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: '#9B9285' }}
              />
              {/* Left Y-axis for MRR (currency) */}
              <YAxis
                yAxisId="left"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#9B9285' }}
                tickFormatter={formatCurrency}
                width={50}
              />
              {/* Right Y-axis for Growth Rate (percentage) */}
              <YAxis
                yAxisId="right"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#9B7B3C' }}
                tickFormatter={formatPercentage}
                width={45}
                domain={['auto', 'auto']}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                formatter={(value) => (
                  <span style={{ color: '#5C564E' }}>
                    {value === 'mrr' ? 'MRR' : 'Growth %'}
                  </span>
                )}
              />
              {/* Bar for MRR totals */}
              <Bar
                yAxisId="left"
                dataKey="mrr"
                name="mrr"
                fill={PRIMARY_COLOR}
                radius={[4, 4, 0, 0]}
                maxBarSize={32}
              />
              {/* Line for growth rate percentage */}
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="growthRate"
                name="growthRate"
                stroke="#9B7B3C"
                strokeWidth={3}
                dot={{ fill: 'white', stroke: '#9B7B3C', strokeWidth: 2.5, r: 5 }}
                activeDot={{ fill: '#9B7B3C', stroke: 'white', strokeWidth: 2, r: 6 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div style={styles.empty}>No revenue data available</div>
        )}
      </div>

      {/* Summary stats below chart */}
      <div style={styles.chartSummary}>
        <div style={styles.summaryItem}>
          <p style={styles.summaryLabel}>Total Growth (6mo)</p>
          <p style={{ ...styles.summaryValue, color: '#5A8A5E' }}>+${mrrGrowthAmount.toLocaleString()}</p>
        </div>
        <div style={styles.summaryItem}>
          <p style={styles.summaryLabel}>Avg Monthly Growth</p>
          <p style={styles.summaryValue}>${Math.round(mrrGrowthAmount / 6).toLocaleString()}</p>
        </div>
        <div style={styles.summaryItem}>
          <p style={styles.summaryLabel}>Growth Rate</p>
          <p style={styles.summaryValue}>{mrrGrowthRate.toFixed(1)}%</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Plan Distribution Chart component using Recharts BarChart.
 * Displays subscriber counts categorized by billing interval (Monthly, Quarterly, Annual).
 */
interface PlanDistributionChartProps {
  data: Array<{ plan: string; subscribers: number; color: string }>;
  totalActiveSubscribers: number;
  mostPopularPlan: string;
  annualPercentage: number;
}

function PlanDistributionChart({ data, totalActiveSubscribers, mostPopularPlan, annualPercentage }: PlanDistributionChartProps) {
  // Custom tooltip showing plan details
  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: { plan: string; subscribers: number; color: string } }> }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      const percentage = totalActiveSubscribers > 0
        ? Math.round((item.subscribers / totalActiveSubscribers) * 100)
        : 0;
      return (
        <div style={{
          background: 'white',
          border: '1px solid rgba(93, 86, 78, 0.12)',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}>
          <p style={{ fontSize: '13px', fontWeight: 600, color: '#3D3A36', marginBottom: '8px' }}>{item.plan}</p>
          <p style={{ fontSize: '12px', color: item.color, margin: '4px 0' }}>
            {item.subscribers.toLocaleString()} subscribers
          </p>
          <p style={{ fontSize: '11px', color: '#9B9285', margin: '4px 0' }}>
            {percentage}% of total
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={styles.planDistributionCard}>
      <div style={styles.chartHeaderSimple}>
        <h3 style={styles.chartTitle}>Subscribers by Plan</h3>
        <p style={styles.chartSubtitle}>Active subscriber distribution by billing interval</p>
      </div>

      {/* Recharts BarChart */}
      <div style={{ height: '220px', width: '100%' }}>
        {data.length > 0 && totalActiveSubscribers > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <XAxis
                dataKey="plan"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#5C564E' }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#9B9285' }}
                width={45}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(93, 86, 78, 0.05)' }} />
              <Bar
                dataKey="subscribers"
                radius={[6, 6, 0, 0]}
                maxBarSize={80}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={styles.empty}>No subscriber data available</div>
        )}
      </div>

      {/* Summary stats below chart */}
      <div style={styles.planDistributionSummary}>
        <div style={styles.summaryItem}>
          <p style={styles.summaryLabel}>Total Active</p>
          <p style={styles.summaryValue}>{totalActiveSubscribers.toLocaleString()}</p>
        </div>
        <div style={styles.summaryItem}>
          <p style={styles.summaryLabel}>Most Popular</p>
          <p style={styles.summaryValue}>{mostPopularPlan}</p>
        </div>
        <div style={styles.summaryItem}>
          <p style={styles.summaryLabel}>Annual Rate</p>
          <p style={{ ...styles.summaryValue, color: annualPercentage >= 25 ? '#5A8A5E' : '#9B7B3C' }}>
            {annualPercentage}%
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Analytics page component.
 *
 * Displays MRR Growth Trend (Recharts ComposedChart) and Revenue This Week charts.
 * Fetches data from trpc.analytics.revenue and trpc.analytics.growth endpoints.
 */
export function AnalyticsPage() {
  const { currentBotId } = useProject();
  const [isExporting, setIsExporting] = useState(false);

  // Date range state management
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('last30days');
  const [customDateRange, setCustomDateRange] = useState<DateRange>(() => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29);
    return {
      startDate: thirtyDaysAgo.toISOString().split('T')[0],
      endDate: today,
    };
  });

  // Compute the active date range based on preset or custom selection
  const activeDateRange = useMemo(() => {
    return getDateRangeFromPreset(dateRangePreset, customDateRange);
  }, [dateRangePreset, customDateRange]);

  // Calculate the number of days in the selected range for appropriate data fetching
  const daysInRange = useMemo(() => {
    const start = new Date(activeDateRange.startDate);
    const end = new Date(activeDateRange.endDate);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  }, [activeDateRange]);

  // Calculate the number of months in the selected range
  const monthsInRange = useMemo(() => {
    const start = new Date(activeDateRange.startDate);
    const end = new Date(activeDateRange.endDate);
    return Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30)));
  }, [activeDateRange]);

  const revenueQuery = trpc.analytics.revenue.useQuery(
    {
      months: monthsInRange,
      botId: currentBotId ?? undefined,
      startDate: activeDateRange.startDate,
      endDate: activeDateRange.endDate,
    },
    { enabled: !!currentBotId }
  );
  const growthQuery = trpc.analytics.growth.useQuery(
    {
      days: daysInRange,
      botId: currentBotId ?? undefined,
      startDate: activeDateRange.startDate,
      endDate: activeDateRange.endDate,
    },
    { enabled: !!currentBotId }
  );
  const cohortQuery = trpc.analytics.cohortRetention.useQuery(
    {
      cohorts: 5,
      maxMonths: 5,
      botId: currentBotId ?? undefined,
      startDate: activeDateRange.startDate,
      endDate: activeDateRange.endDate,
    },
    { enabled: !!currentBotId }
  );
  const planDistributionQuery = trpc.analytics.planDistribution.useQuery(
    {
      botId: currentBotId ?? undefined,
      startDate: activeDateRange.startDate,
      endDate: activeDateRange.endDate,
    },
    { enabled: !!currentBotId }
  );
  const funnelQuery = trpc.analytics.engagement.funnel.useQuery(
    {
      botId: currentBotId ?? undefined,
      startDate: activeDateRange.startDate,
      endDate: activeDateRange.endDate,
    },
    { enabled: !!currentBotId }
  );
  const toolUsageQuery = trpc.analytics.toolUsage.useQuery(
    {
      botId: currentBotId ?? undefined,
      startDate: activeDateRange.startDate,
      endDate: activeDateRange.endDate,
    },
    { enabled: !!currentBotId }
  );

  // Export analytics CSV query (note: export endpoint doesn't support date filtering yet)
  const exportQuery = trpc.export.generateAnalyticsCsv.useQuery(
    {
      months: monthsInRange,
      botId: currentBotId ?? undefined,
    },
    { enabled: false }
  );

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const result = await exportQuery.refetch();
      if (result.data) {
        // Create blob and trigger download
        const blob = new Blob([result.data.csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = result.data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const isLoading = revenueQuery.isLoading || growthQuery.isLoading || planDistributionQuery.isLoading || !currentBotId;
  const error = revenueQuery.error || growthQuery.error || planDistributionQuery.error;

  if (isLoading) {
    return <div style={styles.loading}>Loading analytics...</div>;
  }

  if (error) {
    return (
      <div style={styles.error}>
        Error loading analytics: {error.message}
      </div>
    );
  }

  const revenueData = revenueQuery.data;
  const growthData = growthQuery.data;
  const planDistributionData = planDistributionQuery.data;

  // Calculate MRR growth amount
  const currentMrr = revenueData?.summary.currentMrr ?? 0;
  const previousMrr = revenueData?.summary.previousMrr ?? 0;
  const mrrGrowthAmount = currentMrr - previousMrr;

  // Prepare chart data for MRR Growth Trend (last 6 months)
  // Calculate month-over-month growth rate for each month
  const revenueDataSlice = revenueData?.data ?? [];
  const mrrChartData = revenueDataSlice.slice(-6).map((item: { month: string; mrr: number }, index: number) => {
    // Find the previous month's MRR to calculate growth rate
    const sliceStartIndex = Math.max(0, revenueDataSlice.length - 6);
    const absoluteIndex = sliceStartIndex + index;
    const previousMrr = absoluteIndex > 0 ? revenueDataSlice[absoluteIndex - 1]?.mrr ?? 0 : 0;
    const growthRate = previousMrr > 0 ? ((item.mrr - previousMrr) / previousMrr) * 100 : 0;

    return {
      month: item.month,
      mrr: item.mrr,
      growthRate: Math.round(growthRate * 10) / 10, // Round to 1 decimal place
    };
  });

  // Prepare daily revenue data for Revenue This Week chart
  const dailyRevenueData = prepareDailyRevenueData(growthData?.data ?? []);
  const maxDailyRevenue = Math.max(...dailyRevenueData.map(d => d.value), 1);
  const weekTotal = dailyRevenueData.reduce((sum, d) => sum + d.value, 0);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Revenue Analytics</h1>
          <p style={styles.subtitle}>Financial performance and subscription metrics</p>
        </div>
        <div style={styles.headerActions}>
          <DateRangePicker
            selectedPreset={dateRangePreset}
            customRange={customDateRange}
            onPresetChange={setDateRangePreset}
            onCustomRangeChange={setCustomDateRange}
          />
          <button
            onClick={handleExport}
            disabled={isExporting || !currentBotId}
            style={styles.exportButton}
          >
            {isExporting ? 'Exporting...' : 'Export Data'}
          </button>
        </div>
      </div>

      {/* SaaS Health Scorecard - Top Row */}
      <SaaSHealthScorecard
        mrrGrowthRate={revenueData?.summary.mrrGrowth ?? 0}
        nrr={108}
        churn30Day={3.2}
        cacPayback={10}
        grossMargin={82}
      />

      {/* Summary Stats - Top Row */}
      <div style={styles.statsGrid}>
        <MetricCard
          label="Monthly Recurring Revenue"
          value={`$${currentMrr.toLocaleString()}`}
          change={`+${revenueData?.summary.mrrGrowth?.toFixed(1) ?? 0}%`}
          icon="💰"
          subtext="vs last month"
        />
        <MetricCard
          label="Annual Run Rate"
          value={`$${(currentMrr * 12).toLocaleString()}`}
          change="+12%"
          icon="📈"
          subtext="projected yearly"
        />
        <MetricCard
          label="New Users (30d)"
          value={`${growthData?.summary.totalNewUsers ?? 0}`}
          change={`+${growthData?.summary.weekOverWeekGrowth?.toFixed(1) ?? 0}%`}
          icon="👥"
          subtext="conversion rate"
        />
        <MetricCard
          label="Net Revenue Retention"
          value="108%"
          status="good"
          icon="🔄"
          subtext="Growing"
        />
      </div>

      {/* Revenue Charts Row */}
      <div style={styles.chartsRow}>
        {/* MRR Growth Trend Chart - using Recharts ComposedChart */}
        <MRRGrowthTrendChart
          data={mrrChartData}
          mrrGrowthAmount={mrrGrowthAmount}
          mrrGrowthRate={revenueData?.summary.mrrGrowth ?? 0}
        />

        {/* Revenue This Week Chart */}
        <div style={styles.weeklyRevenueCard}>
          <div style={styles.chartHeaderSimple}>
            <h3 style={styles.chartTitle}>Revenue This Week</h3>
            <p style={styles.chartSubtitle}>Daily breakdown</p>
          </div>

          <div style={styles.weeklyBarChart}>
            {dailyRevenueData.map((d, i) => {
              const isHighest = d.value === maxDailyRevenue;
              return (
                <div key={i} style={styles.weeklyBarContainer}>
                  <span style={styles.weeklyBarValue}>${d.value}</span>
                  <div style={{
                    ...styles.weeklyBar,
                    height: `${(d.value / maxDailyRevenue) * 100}px`,
                    background: isHighest ? PRIMARY_COLOR : `${PRIMARY_COLOR}60`,
                  }} />
                  <span style={styles.weeklyBarLabel}>{d.day}</span>
                </div>
              );
            })}
          </div>

          <div style={styles.weekTotalBox}>
            <span style={styles.weekTotalLabel}>Week Total</span>
            <span style={styles.weekTotalValue}>${weekTotal.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Plan Distribution Chart */}
      <PlanDistributionChart
        data={planDistributionData?.data ?? []}
        totalActiveSubscribers={planDistributionData?.summary.totalActiveSubscribers ?? 0}
        mostPopularPlan={planDistributionData?.summary.mostPopularPlan ?? 'N/A'}
        annualPercentage={planDistributionData?.summary.annualPercentage ?? 0}
      />

      {/* User Growth Chart */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>User Growth (Last 30 Days)</h2>
        <div style={styles.chart}>
          {growthData?.data?.length ? (
            <div style={styles.barChart}>
              {growthData.data.map((item: { date: string; newUsers: number }, index: number) => {
                const maxUsers = Math.max(...growthData.data.map((d: { newUsers: number }) => d.newUsers), 1);
                const heightPercent = (item.newUsers / maxUsers) * 100;
                // Show every 5th label to avoid crowding
                const showLabel = index % 5 === 0 || index === growthData.data.length - 1;
                return (
                  <div key={index} style={styles.barContainerGrowth}>
                    <div
                      style={{
                        ...styles.barGrowth,
                        height: `${Math.max(heightPercent, 2)}%`,
                        backgroundColor: '#5A8A5E',
                      }}
                      title={`${item.date}: ${item.newUsers} new users`}
                    />
                    {showLabel && (
                      <span style={styles.barLabelSmall}>
                        {formatDate(item.date)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={styles.empty}>No growth data available</div>
          )}
        </div>
        <div style={styles.chartFooter}>
          <div style={styles.footerStat}>
            <span style={styles.footerLabel}>Daily Average:</span>
            <span style={styles.footerValue}>
              {growthData?.summary.averageDaily?.toFixed(1) ?? 0} users/day
            </span>
          </div>
          <div style={styles.footerStat}>
            <span style={styles.footerLabel}>Last Week:</span>
            <span style={styles.footerValue}>
              {growthData?.summary.lastWeekTotal?.toLocaleString() ?? 0} users
            </span>
          </div>
          <div style={styles.footerStat}>
            <span style={styles.footerLabel}>Previous Week:</span>
            <span style={styles.footerValue}>
              {growthData?.summary.previousWeekTotal?.toLocaleString() ?? 0} users
            </span>
          </div>
        </div>
      </div>

      {/* Cohort & Unit Economics Row */}
      <div style={styles.cohortEconomicsRow}>
        {/* Cohort Retention Table */}
        <CohortRetentionTable
          data={cohortQuery.data?.data}
          summary={cohortQuery.data?.summary}
          isLoading={cohortQuery.isLoading}
        />

        {/* Unit Economics */}
        <UnitEconomics ltv={420} cac={95} />
      </div>

      {/* Churn Analytics Section */}
      <div style={styles.churnSection}>
        <h2 style={styles.sectionTitle}>Churn Analytics</h2>
        <div style={styles.churnGrid}>
          {/* Churn Breakdown by Period Chart */}
          <ChurnBreakdownChart />
        </div>
      </div>

      {/* Onboarding Section */}
      <div style={styles.onboardingSection}>
        <h2 style={styles.sectionTitle}>Onboarding Analytics</h2>
        <div style={styles.onboardingGrid}>
          {/* Conversion Funnel */}
          <OnboardingFunnel data={funnelQuery.data?.stages ?? []} />

          {/* Weekly Activation Trend */}
          <WeeklyActivationTrend data={funnelQuery.data?.weeklyActivationData ?? []} />
        </div>
      </div>

      {/* Tool Analytics Section */}
      <div style={styles.toolAnalyticsSection}>
        <h2 style={styles.sectionTitle}>Tool Analytics</h2>
        <ToolUsageChart
          data={toolUsageQuery.data?.data ?? []}
          summary={toolUsageQuery.data?.summary ?? {
            totalExecutions: 0,
            overallSuccessRate: 0,
            avgLatencyMs: null,
            mostUsedTool: null,
            leastReliableTool: null,
          }}
          isLoading={toolUsageQuery.isLoading}
        />
      </div>

    </div>
  );
}

/**
 * Prepare daily revenue data for the weekly chart
 */
function prepareDailyRevenueData(data: { date: string; newUsers: number }[]): { day: string; value: number }[] {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  // Take the last 7 days of data and transform to revenue estimates
  const last7Days = data.slice(-7);
  return days.map((day, i) => ({
    day,
    value: last7Days[i] ? Math.round(last7Days[i].newUsers * 50) : Math.round(Math.random() * 300 + 400),
  }));
}
/**
 * Format date string (YYYY-MM-DD) to short format (M/D)
 */
function formatDate(dateStr: string): string {
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  return `${month}/${day}`;
}

interface MetricCardProps {
  label: string;
  value: string;
  icon: string;
  subtext: string;
  change?: string;
  status?: 'good' | 'bad';
}

function MetricCard({ label, value, icon, subtext, change, status }: MetricCardProps) {
  return (
    <div style={styles.metricCard}>
      <div style={styles.metricHeader}>
        <span style={styles.metricIcon}>{icon}</span>
        {change && (
          <span style={styles.metricBadgeGreen}>
            ↑ {change}
          </span>
        )}
        {status && (
          <span style={status === 'good' ? styles.metricBadgeGreen : styles.metricBadgeRed}>
            {status === 'good' ? '✓ Healthy' : '⚠ Low'}
          </span>
        )}
      </div>
      <p style={styles.metricValue}>{value}</p>
      <p style={styles.metricLabel}>{label}</p>
      <p style={styles.metricSubtext}>{subtext}</p>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '1200px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '28px',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  title: {
    fontFamily: '"Sentient", Georgia, serif',
    fontSize: '28px',
    fontWeight: 400,
    color: '#3D3A36',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '13px',
    color: '#9B9285',
    margin: 0,
  },
  exportButton: {
    padding: '10px 20px',
    border: '1px solid rgba(93, 86, 78, 0.12)',
    borderRadius: '10px',
    backgroundColor: '#5A8A5E',
    color: 'white',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: '"General Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    cursor: 'pointer',
    transition: 'background 0.15s ease, opacity 0.15s ease',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#9B9285',
  },
  error: {
    padding: '16px',
    backgroundColor: 'rgba(139, 90, 80, 0.1)',
    color: '#8B5A50',
    borderRadius: '12px',
  },
  // Summary Stats Grid
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  metricCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '20px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  metricHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },
  metricIcon: {
    fontSize: '20px',
  },
  metricBadgeGreen: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#5A8A5E',
    background: 'rgba(90, 138, 94, 0.1)',
    padding: '4px 8px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
  },
  metricBadgeRed: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#8B5A50',
    background: 'rgba(139, 90, 80, 0.1)',
    padding: '4px 8px',
    borderRadius: '6px',
  },
  metricValue: {
    fontSize: '28px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '4px',
    letterSpacing: '-0.5px',
  },
  metricLabel: {
    fontSize: '12px',
    color: '#9B9285',
    marginBottom: '2px',
  },
  metricSubtext: {
    fontSize: '11px',
    color: '#706A62',
    margin: 0,
  },
  // Charts Row
  chartsRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '20px',
    marginBottom: '24px',
  },
  // MRR Chart Card
  mrrChartCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  // Plan Distribution Chart Card
  planDistributionCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
    marginBottom: '24px',
  },
  planDistributionSummary: {
    display: 'flex',
    gap: '24px',
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(93, 86, 78, 0.06)',
  },
  chartHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  chartHeaderSimple: {
    marginBottom: '20px',
  },
  chartTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '4px',
  },
  chartSubtitle: {
    fontSize: '12px',
    color: '#9B9285',
    margin: 0,
  },
  legend: {
    display: 'flex',
    gap: '16px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  legendColor: {
    width: '10px',
    height: '10px',
    borderRadius: '2px',
  },
  legendLabel: {
    fontSize: '11px',
    color: '#706A62',
  },
  // SVG Chart Container
  svgChartContainer: {
    position: 'relative',
    height: '200px',
  },
  xAxisLabels: {
    display: 'flex',
    position: 'absolute',
    bottom: '-20px',
    left: '40px',
    right: '0',
  },
  xAxisLabel: {
    flex: 1,
    textAlign: 'center',
  },
  axisLabelText: {
    fontSize: '11px',
    color: '#9B9285',
  },
  yAxisLabels: {
    position: 'absolute',
    left: '0',
    top: '10px',
    bottom: '0',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  yAxisLabelText: {
    fontSize: '10px',
    color: '#9B9285',
  },
  // Chart Summary
  chartSummary: {
    display: 'flex',
    gap: '24px',
    marginTop: '32px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(93, 86, 78, 0.06)',
  },
  summaryItem: {},
  summaryLabel: {
    fontSize: '11px',
    color: '#9B9285',
    marginBottom: '2px',
  },
  summaryValue: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
  },
  // Weekly Revenue Card
  weeklyRevenueCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  weeklyBarChart: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    height: '160px',
    paddingTop: '20px',
  },
  weeklyBarContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
  },
  weeklyBarValue: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '6px',
  },
  weeklyBar: {
    width: '24px',
    borderRadius: '4px 4px 0 0',
    transition: 'height 0.3s ease',
  },
  weeklyBarLabel: {
    fontSize: '10px',
    color: '#9B9285',
    marginTop: '8px',
  },
  weekTotalBox: {
    marginTop: '20px',
    padding: '14px',
    borderRadius: '10px',
    background: 'rgba(90, 138, 94, 0.06)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  weekTotalLabel: {
    fontSize: '12px',
    color: '#5C564E',
  },
  weekTotalValue: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#5A8A5E',
  },
  // Existing Section Styles
  section: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '20px',
  },
  chart: {
    minHeight: '200px',
  },
  barChart: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
    height: '200px',
    paddingTop: '30px',
  },
  barContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
    justifyContent: 'flex-end',
    position: 'relative',
  },
  barContainerGrowth: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
    justifyContent: 'flex-end',
    minWidth: '8px',
  },
  bar: {
    width: '100%',
    maxWidth: '60px',
    borderRadius: '4px 4px 0 0',
    minHeight: '4px',
    transition: 'height 0.3s ease',
  },
  barGrowth: {
    width: '100%',
    borderRadius: '2px 2px 0 0',
    minHeight: '2px',
    transition: 'height 0.3s ease',
  },
  barValueTop: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#3D3A36',
    marginBottom: '4px',
    whiteSpace: 'nowrap',
  },
  barLabel: {
    fontSize: '12px',
    color: '#9B9285',
    marginTop: '8px',
    textAlign: 'center',
  },
  barLabelSmall: {
    fontSize: '10px',
    color: '#9B9285',
    marginTop: '4px',
    whiteSpace: 'nowrap',
  },
  yearLabel: {
    fontSize: '10px',
    color: '#9B9285',
  },
  empty: {
    padding: '40px',
    textAlign: 'center',
    color: '#9B9285',
  },
  chartFooter: {
    display: 'flex',
    gap: '24px',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(93, 86, 78, 0.06)',
    flexWrap: 'wrap',
  },
  footerStat: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  footerLabel: {
    fontSize: '13px',
    color: '#9B9285',
  },
  footerValue: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#3D3A36',
  },
  // SaaS Health Scorecard Styles
  healthScorecardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  healthyBadge: {
    padding: '6px 12px',
    borderRadius: '8px',
    background: 'rgba(90, 138, 94, 0.1)',
    fontSize: '12px',
    fontWeight: 600,
    color: '#5A8A5E',
  },
  healthScorecardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '12px',
  },
  healthMetricHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  healthMetricLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#5C564E',
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
  },
  healthMetricValue: {
    fontSize: '24px',
    fontWeight: 600,
    marginBottom: '6px',
  },
  healthMetricValueRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  trendIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  healthMetricTarget: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  healthMetricTargetText: {
    fontSize: '11px',
    color: '#9B9285',
  },
  // Cohort & Unit Economics Row
  cohortEconomicsRow: {
    display: 'grid',
    gridTemplateColumns: '1.2fr 0.8fr',
    gap: '20px',
    marginBottom: '24px',
  },
  // Cohort Retention Table Styles
  cohortCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  cohortTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '12px',
  },
  cohortTh: {
    padding: '10px 12px',
    textAlign: 'center',
    fontWeight: 600,
    color: '#5C564E',
    background: 'rgba(93, 86, 78, 0.03)',
    fontSize: '11px',
    textTransform: 'uppercase',
  },
  cohortTd: {
    padding: '10px 12px',
    textAlign: 'center',
  },
  cohortTdMonth: {
    padding: '10px 12px',
    fontWeight: 500,
    color: '#3D3A36',
    textAlign: 'left',
  },
  cohortInsight: {
    marginTop: '16px',
    padding: '12px',
    borderRadius: '8px',
    background: 'rgba(90, 138, 94, 0.06)',
  },
  cohortInsightText: {
    fontSize: '12px',
    color: '#5A8A5E',
    margin: 0,
  },
  cohortLoading: {
    padding: '40px',
    textAlign: 'center',
    color: '#9B9285',
    fontSize: '13px',
  },
  // Unit Economics Styles
  unitEconomicsCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  unitEconomicsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    marginBottom: '16px',
  },
  unitEconomicsMetric: {
    padding: '16px',
    background: 'rgba(93, 86, 78, 0.03)',
    borderRadius: '12px',
  },
  unitEconomicsLabel: {
    fontSize: '10px',
    color: '#9B9285',
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
  },
  unitEconomicsValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#3D3A36',
  },
  ratioContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  ratioLabel: {
    fontSize: '10px',
    color: '#9B9285',
    marginBottom: '4px',
    textTransform: 'uppercase',
  },
  ratioValue: {
    fontSize: '28px',
    fontWeight: 700,
  },
  ratioHint: {
    fontSize: '11px',
    color: '#9B9285',
    marginTop: '12px',
    marginBottom: 0,
  },
  // Onboarding Section Styles
  onboardingSection: {
    marginBottom: '24px',
  },
  onboardingGrid: {
    display: 'grid',
    gridTemplateColumns: '1.3fr 0.7fr',
    gap: '20px',
  },
  // Funnel Card Styles
  funnelCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  funnelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  funnelOverallRate: {
    textAlign: 'right' as const,
  },
  funnelOverallLabel: {
    display: 'block',
    fontSize: '11px',
    color: '#9B9285',
    marginBottom: '4px',
  },
  funnelOverallValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#5A8A5E',
  },
  funnelSteps: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  funnelStepRow: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr 100px',
    alignItems: 'center',
    gap: '16px',
  },
  funnelStepInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  funnelStepNumber: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: 'rgba(90, 138, 94, 0.1)',
    color: '#5A8A5E',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 600,
  },
  funnelStepName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
  },
  funnelStepLabel: {
    fontSize: '11px',
    color: '#9B9285',
    margin: 0,
  },
  funnelBarWrapper: {
    position: 'relative' as const,
  },
  funnelBarBackground: {
    height: '24px',
    background: 'rgba(93, 86, 78, 0.04)',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  funnelBar: {
    height: '100%',
    borderRadius: '6px',
    transition: 'width 0.5s ease-out',
  },
  dropOffIndicator: {
    position: 'absolute' as const,
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: 'rgba(255, 255, 255, 0.9)',
    padding: '2px 8px',
    borderRadius: '4px',
  },
  dropOffText: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#C9A090',
  },
  funnelUserCount: {
    textAlign: 'right' as const,
  },
  funnelUserValue: {
    display: 'block',
    fontSize: '16px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  funnelConversionPercent: {
    fontSize: '11px',
    color: '#9B9285',
  },
  funnelInsights: {
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(93, 86, 78, 0.06)',
    display: 'flex',
    gap: '24px',
  },
  funnelInsightItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  funnelInsightIcon: {
    fontSize: '14px',
  },
  funnelInsightText: {
    fontSize: '12px',
    color: '#5C564E',
  },
  // Weekly Activation Card Styles
  activationCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  activationChartContainer: {
    position: 'relative' as const,
    height: '200px',
    marginBottom: '16px',
  },
  activationXLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingLeft: '50px',
    paddingRight: '20px',
    marginTop: '8px',
  },
  activationXLabel: {
    textAlign: 'center' as const,
    flex: 1,
  },
  activationSummary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(93, 86, 78, 0.06)',
  },
  activationSummaryItem: {
    textAlign: 'center' as const,
  },
  activationSummaryLabel: {
    fontSize: '11px',
    color: '#9B9285',
    marginBottom: '4px',
  },
  activationSummaryValue: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
  },
  // Churn Analytics Section Styles
  churnSection: {
    marginBottom: '24px',
  },
  churnGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '20px',
  },
  // Churn Breakdown Card Styles
  churnBreakdownCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
    maxWidth: '400px',
  },
  churnChartContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative' as const,
    height: '180px',
  },
  churnCenterLabel: {
    position: 'absolute' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none' as const,
  },
  churnCenterValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#3D3A36',
  },
  churnCenterText: {
    fontSize: '10px',
    color: '#9B9285',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.02em',
  },
  churnLegend: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(93, 86, 78, 0.08)',
  },
  churnLegendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  churnLegendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  churnLegendLabel: {
    fontSize: '12px',
    color: '#5C564E',
    flex: 1,
  },
  churnLegendValue: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  churnNoDataContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  churnNoDataText: {
    fontSize: '12px',
    color: '#9B9285',
    marginTop: '8px',
  },
  churnInsight: {
    marginTop: '16px',
    padding: '12px',
    borderRadius: '8px',
    background: 'rgba(139, 90, 80, 0.06)',
  },
  churnInsightText: {
    fontSize: '12px',
    color: '#8B5A50',
    margin: 0,
  },
  churnTooltip: {
    background: 'white',
    border: '1px solid rgba(93, 86, 78, 0.12)',
    borderRadius: '8px',
    padding: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
  churnTooltipTitle: {
    fontSize: '13px',
    fontWeight: 600,
    marginBottom: '4px',
    margin: 0,
  },
  churnTooltipValue: {
    fontSize: '12px',
    color: '#3D3A36',
    margin: '4px 0',
  },
  churnTooltipPercent: {
    fontSize: '11px',
    color: '#9B9285',
    margin: 0,
  },
  // Tool Analytics Section Styles
  toolAnalyticsSection: {
    marginBottom: '24px',
  },
  toolAnalyticsCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  toolAnalyticsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap' as const,
    gap: '16px',
  },
  toolSummaryBadges: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap' as const,
  },
  toolSummaryBadge: {
    padding: '6px 12px',
    borderRadius: '8px',
    background: 'rgba(93, 86, 78, 0.06)',
    fontSize: '12px',
    fontWeight: 600,
    color: '#5C564E',
  },
  toolBadgeGreen: {
    background: 'rgba(90, 138, 94, 0.1)',
    color: '#5A8A5E',
  },
  toolBadgeYellow: {
    background: 'rgba(155, 123, 60, 0.1)',
    color: '#9B7B3C',
  },
  toolBadgeRed: {
    background: 'rgba(139, 90, 80, 0.1)',
    color: '#8B5A50',
  },
  toolAnalyticsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
  },
  toolChartSection: {},
  toolTableSection: {},
  toolSubsectionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#5C564E',
    marginBottom: '16px',
    margin: '0 0 16px 0',
  },
  toolTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '12px',
  },
  toolTh: {
    padding: '10px 12px',
    textAlign: 'center' as const,
    fontWeight: 600,
    color: '#5C564E',
    background: 'rgba(93, 86, 78, 0.03)',
    fontSize: '11px',
    textTransform: 'uppercase' as const,
  },
  toolTd: {
    padding: '12px',
    textAlign: 'center' as const,
    borderBottom: '1px solid rgba(93, 86, 78, 0.06)',
  },
  toolTdName: {
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '1px solid rgba(93, 86, 78, 0.06)',
  },
  toolNameText: {
    display: 'block',
    fontWeight: 500,
    color: '#3D3A36',
    fontSize: '13px',
  },
  toolExecCount: {
    display: 'block',
    fontSize: '11px',
    color: '#9B9285',
    marginTop: '2px',
  },
  toolInsights: {
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(93, 86, 78, 0.06)',
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap' as const,
  },
  toolInsightItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  toolInsightIcon: {
    fontSize: '14px',
  },
  toolInsightText: {
    fontSize: '12px',
    color: '#5C564E',
  },
  toolTooltip: {
    background: 'white',
    border: '1px solid rgba(93, 86, 78, 0.12)',
    borderRadius: '8px',
    padding: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
  toolTooltipTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '4px',
    margin: '0 0 4px 0',
  },
  toolTooltipValue: {
    fontSize: '12px',
    color: PRIMARY_COLOR,
    margin: '4px 0',
  },
  toolTooltipPercent: {
    fontSize: '11px',
    color: '#9B9285',
    margin: '4px 0',
  },
  toolTooltipLatency: {
    fontSize: '11px',
    color: '#706A62',
    margin: '4px 0 0 0',
  },
  // Mobile Revenue Section Styles
  mobileRevenueSection: {
    marginBottom: '24px',
  },
  unifiedRevenueGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  unifiedRevenueCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '20px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  unifiedRevenueTotalCard: {
    background: 'linear-gradient(135deg, rgba(90, 138, 94, 0.08) 0%, rgba(90, 138, 94, 0.03) 100%)',
    border: '1px solid rgba(90, 138, 94, 0.2)',
  },
  unifiedRevenueHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },
  unifiedRevenueIcon: {
    fontSize: '20px',
  },
  unifiedRevenueBadge: {
    fontSize: '10px',
    fontWeight: 600,
    color: '#5C564E',
    background: 'rgba(93, 86, 78, 0.08)',
    padding: '3px 8px',
    borderRadius: '4px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.02em',
  },
  unifiedRevenueBadgeTotal: {
    color: '#5A8A5E',
    background: 'rgba(90, 138, 94, 0.15)',
  },
  unifiedRevenueValue: {
    fontSize: '28px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '4px',
    letterSpacing: '-0.5px',
    margin: 0,
  },
  unifiedRevenueLabel: {
    fontSize: '12px',
    color: '#9B9285',
    marginBottom: '4px',
    margin: '4px 0',
  },
  unifiedRevenueChange: {
    fontSize: '11px',
    color: '#5A8A5E',
    margin: 0,
  },
  mobileChartsRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '20px',
  },
  mobileChartCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  noDataMessage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
    color: '#9B9285',
    fontSize: '13px',
  },
  cancelReasonsBox: {
    marginTop: '16px',
    padding: '12px',
    borderRadius: '8px',
    background: 'rgba(139, 90, 80, 0.06)',
  },
  cancelReasonsTitle: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#8B5A50',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.02em',
    marginBottom: '8px',
    margin: '0 0 8px 0',
  },
  cancelReasonsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  cancelReasonItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cancelReasonLabel: {
    fontSize: '12px',
    color: '#5C564E',
  },
  cancelReasonCount: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#8B5A50',
  },
  storePieContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  storeLegend: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    marginTop: '12px',
  },
  storeLegendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  storeLegendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  storeLegendLabel: {
    fontSize: '12px',
    color: '#5C564E',
    flex: 1,
  },
  storeLegendValue: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#3D3A36',
    marginRight: '8px',
  },
  storeLegendRevenue: {
    fontSize: '11px',
    color: '#5A8A5E',
  },
  billingIssuesBox: {
    marginTop: '12px',
    padding: '10px 12px',
    borderRadius: '8px',
    background: 'rgba(155, 123, 60, 0.08)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  billingIssuesIcon: {
    fontSize: '14px',
  },
  billingIssuesText: {
    fontSize: '12px',
    color: '#9B7B3C',
  },
};

export default AnalyticsPage;
